import React, { useEffect, useMemo, useState } from 'react';
import { Search, Sparkles, ArrowRight, LoaderCircle, Route } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import bisSchemeOneData from '../data/bisSchemeOneProducts.json';
import beeProductData from '../data/beeProducts.json';

const authorityRoutes = {
  BIS: '/bureau-of-indian-standards-bis',
  'BIS / STQC': '/bureau-of-indian-standards-bis',
  WPC: '/wpc-approvals-and-licenses',
  'TEC / MTCTE': '/tec-mtcte-certification',
  TEC: '/tec-mtcte-certification',
  DOT: '/tac-imei-registration',
  'Legal Metrology': '/legal-metrology-compliance-services',
  BEE: '/bee-certification',
  FSSAI: '/fssai-approval',
  DGFT: '/dgft-approval',
  CDSCO: '/medical-device-registration',
  'EPR / Waste Compliance': '/business-legal-compliance',
  ARAI: '/arai-approval',
  PESO: '/peso-certification',
  STQC: '/stqc-certification',
  GeM: '/gem-registration',
};

const gemServiceEntry = {
  product: 'GeM registration and account management',
  aliases: [
    'government e marketplace',
    'gem registration',
    'gem seller',
    'gem service provider',
    'government tender',
    'government procurement',
    'bid reverse auction',
    'gem catalogue',
    'gem vendor assessment',
    'desktop assessment',
    'video assessment',
    'gem tender management',
    'gem order management',
    'gem account management',
  ],
  productHref: '/gem-registration',
  approvals: [{ authority: 'GeM', approval: 'GeM seller and marketplace support', href: '/gem-registration' }],
  sourceLabel: 'Complise service route',
};

const legalMetrologyServiceEntry = {
  product: 'Legal Metrology compliance services',
  aliases: [
    'lmpc registration',
    'packaged commodities',
    'rule 27 registration',
    'legal metrology label audit',
    'retail package declarations',
    'manufacturer packer importer registration',
    'mrp net quantity country of origin label',
  ],
  productHref: '/legal-metrology-compliance-services',
  approvals: [{ authority: 'Legal Metrology', approval: 'Legal Metrology packaged commodity compliance', href: '/legal-metrology-compliance-services' }],
  sourceLabel: 'Complise service route',
};

const popularProducts = [
  'Bluetooth Speakers',
  'Mobile phone / smart device',
  'Electrical / lighting product',
  'Medical device',
  'Food / beverage product',
  'Automotive / safety component',
];

const normalize = (value = '') => value
  .toLocaleLowerCase()
  .normalize('NFKD')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const searchStopWords = new Set(['a', 'an', 'and', 'for', 'in', 'of', 'the', 'to', 'with']);

const stemWord = (word) => {
  if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.length > 5 && word.endsWith('ing')) return word.slice(0, -3);
  if (word.length > 4 && word.endsWith('es')) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith('s')) return word.slice(0, -1);
  return word;
};

const searchWords = (value = '') => normalize(value)
  .split(' ')
  .filter((word) => word && !searchStopWords.has(word))
  .map(stemWord);

const editDistance = (left, right) => {
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;
  let previous = Array.from({ length: right.length + 1 }, (_value, index) => index);
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    const current = [leftIndex];
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      current[rightIndex] = Math.min(
        current[rightIndex - 1] + 1,
        previous[rightIndex] + 1,
        previous[rightIndex - 1] + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1),
      );
    }
    previous = current;
  }
  return previous[right.length];
};

const wordMatches = (queryWord, candidateWord) => {
  if (candidateWord === queryWord) return true;
  if (queryWord.length >= 3 && (candidateWord.startsWith(queryWord) || candidateWord.includes(queryWord))) return true;
  const tolerance = queryWord.length >= 8 ? 2 : queryWord.length >= 5 ? 1 : 0;
  return tolerance > 0 && Math.abs(candidateWord.length - queryWord.length) <= tolerance
    && editDistance(queryWord, candidateWord) <= tolerance;
};

const directApproval = (route) => {
  if (route.startsWith('/isi-product-')) return { authority: 'BIS', approval: 'BIS ISI Mark', href: '/isi-mark-certification-scheme-1' };
  if (route.startsWith('/crs-product-')) return { authority: 'BIS', approval: 'BIS CRS Registration', href: '/crs-certification' };
  if (route.startsWith('/bee-')) return { authority: 'BEE', approval: 'BEE Registration / Star Label', href: '/bee-certification' };
  if (route.startsWith('/wpc-')) return { authority: 'WPC', approval: 'WPC ETA / Wireless Approval', href: '/wpc-approvals-and-licenses' };
  if (/^\/(tec-|mtcte-|gr-ir-|tsec-|nccs-)/.test(route)) return { authority: 'TEC', approval: 'TEC / MTCTE Certification', href: '/tec-mtcte-certification' };
  if (route.startsWith('/stqc-')) return { authority: 'STQC', approval: 'STQC Certification', href: '/stqc-certification' };
  if (route.startsWith('/lmpc-')) return { authority: 'Legal Metrology', approval: 'LMPC / Packaged Commodity Compliance', href: '/legal-metrology-certification' };
  if (route.startsWith('/cdsco-')) return { authority: 'CDSCO', approval: 'CDSCO Registration', href: '/medical-device-registration' };
  if (route.startsWith('/fssai-')) return { authority: 'FSSAI', approval: 'FSSAI Approval', href: '/fssai-approval' };
  if (route.startsWith('/imei-')) return { authority: 'DOT', approval: 'TAC / IMEI Registration', href: '/tac-imei-registration' };
  if (route.startsWith('/arai-')) return { authority: 'ARAI', approval: 'ARAI Approval', href: '/arai-approval' };
  if (route.startsWith('/peso-')) return { authority: 'PESO', approval: 'PESO Certification', href: '/peso-certification' };
  if (route.startsWith('/dgft-')) return { authority: 'DGFT', approval: 'DGFT Import / Export Approval', href: '/dgft-approval' };
  if (route.startsWith('/hallmark-')) return { authority: 'BIS', approval: 'BIS Hallmark Registration', href: '/bis-hallmark-registration' };
  if (route === '/gem-registration') return { authority: 'GeM', approval: 'GeM seller and marketplace support', href: '/gem-registration' };
  if (route === '/legal-metrology-compliance-services') return { authority: 'Legal Metrology', approval: 'Legal Metrology packaged commodity compliance', href: '/legal-metrology-compliance-services' };
  return null;
};

const additionalApprovals = (name) => {
  const value = normalize(name);
  const routes = [];
  const add = (authority, approval, href) => routes.push({ authority, approval, href });

  if (/bluetooth|wi fi|wifi|wireless|radio|router|modem|iot|drone|rf module/.test(value)) {
    add('WPC', 'WPC ETA / Wireless Approval', '/wpc-approvals-and-licenses');
  }
  if (/telecom|network|router|modem|gateway|optical|ethernet|switch|base station/.test(value)) {
    add('TEC', 'TEC / MTCTE Certification', '/tec-mtcte-certification');
  }
  if (/mobile|smartphone|cellular|imei/.test(value)) {
    add('DOT', 'TAC / IMEI Registration', '/tac-imei-registration');
  }
  if (/electrical|electronic|appliance|adapter|charger|power supply|lamp|light|bulb|fan|cable|wire|switch|socket|toy|helmet|cement|steel|footwear/.test(value)) {
    add('BIS', 'BIS ISI / CRS applicability check', '/bureau-of-indian-standards-bis');
  }
  if (/air conditioner|refrigerator|motor|led lamp|led bulb|appliance/.test(value)) {
    add('BEE', 'BEE Registration / Star Label', '/bee-certification');
  }
  if (/medical|diagnostic|thermometer|glucose|surgical|implant/.test(value)) {
    add('CDSCO', 'Medical Device Registration', '/medical-device-registration');
  }
  if (/food|beverage|snack|drink|supplement|spice|edible/.test(value)) {
    add('FSSAI', 'FSSAI Licence / Product Approval', '/fssai-approval');
  }
  if (/vehicle|automotive|tyre|tire|helmet|brake|two wheeler|car /.test(value)) {
    add('ARAI', 'Automotive Testing / Approval', '/arai-approval');
  }
  if (/gas cylinder|pressure vessel|petroleum|explosive|fuel tank/.test(value)) {
    add('PESO', 'PESO Certification', '/peso-certification');
  }
  if (/battery|power bank|e waste|electronic waste|plastic packaging|packaging/.test(value)) {
    add('EPR / Waste Compliance', 'EPR registration / waste compliance', '/business-legal-compliance');
  }
  if (/cosmetic|cream|shampoo|lotion|makeup|perfume|hair oil/.test(value)) {
    add('CDSCO', 'Cosmetic registration / licence', '/medical-device-registration');
  }
  if (/biometric|smart card|cyber security|cybersecurity|security device/.test(value)) {
    add('STQC', 'STQC testing / certification', '/stqc-certification');
  }
  if (/import|export|restricted goods|foreign trade/.test(value)) {
    add('DGFT', 'DGFT import / export route check', '/dgft-approval');
  }
  if (/government e marketplace|gem registration|gem seller|government tender|government procurement|reverse auction/.test(value)) {
    add('GeM', 'GeM seller and marketplace support', '/gem-registration');
  }
  add('Legal Metrology', 'LMPC / Retail Label Compliance', '/legal-metrology-compliance-services');
  return uniqueApprovals(routes);
};

const titleFromManifest = (item) => item.title
  .replace(/\s+(ISI Certification|CRS Registration|Compliance Route).*$/i, '')
  .replace(/\s*\|.*$/, '')
  .trim();

const topLevelApprovalRoutes = new Set([
  '/bee-certification', '/wpc-approvals-and-licenses', '/tec-mtcte-certification',
  '/stqc-certification', '/fssai-approval', '/peso-certification', '/dgft-approval', '/arai-approval',
  '/gem-registration',
  '/legal-metrology-compliance-services',
]);

const isProductEntry = ({ route = '', category = '' }) => (
  category === 'Product Certifications'
  || (!topLevelApprovalRoutes.has(route)
    && /^\/(isi-product-|crs-product-|bee-|wpc-|tec-|mtcte-|gr-ir-|tsec-|nccs-|stqc-|lmpc-|cdsco-|fssai-|imei-|arai-|peso-|dgft-|hallmark-)/.test(route))
);

const uniqueApprovals = (items) => [...new Map(items.map((item) => [item.approval, item])).values()];

const inferredApprovals = (name) => {
  const approvals = additionalApprovals(name);
  if (approvals.length === 1) {
    approvals.unshift({
      authority: 'BIS',
      approval: 'Product compliance applicability check',
      href: '/bureau-of-indian-standards-bis',
      note: 'Confirm the product specification, intended use and applicable Indian Standard or regulatory notification.',
    });
  }
  return approvals;
};

const AISearch = ({ open, onOpenChange, initialQuery = '' }) => {
  const [query, setQuery] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && initialQuery) setQuery(initialQuery);
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open || catalog.length) return undefined;
    const controller = new AbortController();
    setLoading(true);

    const fetchJson = (url) => fetch(url, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : []))
      .catch((error) => {
        if (error.name === 'AbortError') throw error;
        return [];
      });

    Promise.all([
      fetchJson('/legacy-pages/manifest.json'),
      fetchJson('/legacy-source/assets/data/complise-ai-rules.json'),
    ]).then(([manifest, rules]) => {
      const ruleEntries = rules.map((rule) => ({
        product: rule.title,
        aliases: rule.products,
        productHref: authorityRoutes[rule.compliances[0]?.authority] || '/services',
        approvals: rule.compliances.map((item) => ({
          ...item,
          href: authorityRoutes[item.authority] || '/services',
        })),
        sourceLabel: 'Complise route guidance',
      }));

      const siteProducts = manifest.filter(isProductEntry).map((item) => {
        const product = titleFromManifest(item);
        const primary = directApproval(item.route);
        return {
          product,
          aliases: [product, item.description, item.route.replace(/[-/]/g, ' ')],
          productHref: item.route,
          approvals: uniqueApprovals([primary, ...additionalApprovals(product)].filter(Boolean)),
          sourceLabel: 'Website product guide',
        };
      });

      const availableRoutes = new Set(manifest.map((item) => item.route));
      const isiProductRoutes = new Map(siteProducts
        .filter((item) => item.productHref.startsWith('/isi-product-'))
        .map((item) => [normalize(item.product), item.productHref]));
      const isiTableProducts = bisSchemeOneData.products.map((item) => {
        const linkedRoute = isiProductRoutes.get(normalize(item.product));
        return {
          product: item.product,
          aliases: [item.product, item.standard, item.category, item.notification].filter(Boolean),
          productHref: availableRoutes.has(linkedRoute) ? linkedRoute : item.route,
          approvals: uniqueApprovals([
            { authority: 'BIS', approval: 'BIS ISI Mark', href: '/isi-mark-certification-scheme-1' },
            ...additionalApprovals(`${item.product} ${item.category}`),
          ]),
          standard: item.standard,
          category: item.category,
          sourceLabel: 'Official BIS Scheme-I list',
        };
      });

      const beeOfficialProducts = [...new Map(beeProductData.products.map((item) => {
        const matchingEntries = beeProductData.products.filter((candidate) => candidate.route === item.route);
        return [item.route, {
          product: item.product,
          aliases: matchingEntries.flatMap((entry) => [
            entry.product,
            entry.scheme,
            entry.notificationTitle,
            entry.status,
            ...(entry.aliases || []),
          ]).filter(Boolean),
          productHref: item.route,
          approvals: uniqueApprovals([
            { authority: 'BEE', approval: 'BEE Registration / Star Label', href: '/bee-certification' },
            ...additionalApprovals(`${item.product} ${(item.aliases || []).join(' ')}`),
          ]),
          category: `${[...new Set(matchingEntries.map((entry) => entry.scheme))].join(' / ')} BEE appliance`,
          sourceLabel: 'Official BEE Standards & Labelling list',
        }];
      })).values()];

      const indexedProducts = [...new Map(
        [...siteProducts, ...isiTableProducts, ...beeOfficialProducts].map((item) => [normalize(item.product), item]),
      ).values()];
      setCatalog([gemServiceEntry, legalMetrologyServiceEntry, ...ruleEntries, ...indexedProducts]);
    }).catch((error) => {
      if (error.name !== 'AbortError') setCatalog([]);
    }).finally(() => setLoading(false));

    return () => controller.abort();
  }, [open, catalog.length]);

  const matches = useMemo(() => {
    const normalizedQuery = normalize(query);
    const words = searchWords(query);
    const ranked = catalog.map((item) => {
      const product = normalize(item.product);
      const searchable = normalize([item.product, ...(item.aliases || [])].join(' '));
      const candidateWords = searchWords(searchable);
      const exactWords = words.length > 0 && words.every((word) => candidateWords.includes(word));
      const fuzzyWords = words.length > 0 && words.every((word) => candidateWords.some((candidate) => wordMatches(word, candidate)));
      let score = 0;
      if (product === normalizedQuery) score = 1000;
      else if (product.startsWith(normalizedQuery)) score = 850;
      else if (product.includes(normalizedQuery)) score = 700;
      else if (exactWords) score = 520;
      else if (fuzzyWords) score = 300;
      if (score > 0 && item.sourceLabel === 'Complise route guidance') score += 40;
      return { ...item, score };
    }).filter((item) => item.score > 0).sort((a, b) => b.score - a.score || a.product.localeCompare(b.product));

    if (words.length && ranked.length) return ranked.slice(0, 24);
    if (words.length && query.trim().length >= 2) {
      return [{
        product: `Route assessment for “${query.trim()}”`,
        aliases: [],
        productHref: '/contact',
        approvals: inferredApprovals(query),
        sourceLabel: 'Keyword-based preliminary assessment',
        generated: true,
        score: 1,
      }];
    }
    return popularProducts.map((name) => catalog.find((item) => item.product === name)).filter(Boolean);
  }, [catalog, query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] bg-white border border-neutral-200 rounded-[1.75rem] p-0 overflow-hidden shadow-2xl flex flex-col">
        <div className="p-6 md:p-7 border-b border-neutral-100 flex items-center gap-4 bg-gradient-to-r from-rose-50/80 to-white">
          <span className="w-11 h-11 rounded-2xl bg-[#C41E3A] text-white flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </span>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#C41E3A] font-bold">AI Approval Navigator</div>
            <DialogTitle className="font-display text-xl md:text-2xl font-bold text-neutral-900">Find the compliance route for your product</DialogTitle>
            <DialogDescription className="sr-only">Enter a product name to see its likely regulatory approval and certification route.</DialogDescription>
          </div>
        </div>
        <div className="p-6 md:p-7 overflow-y-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Type any listed product — Bluetooth speaker, webcam, LED lamp…"
              className="h-14 pl-12 rounded-xl border-neutral-300 focus:border-[#C41E3A] bg-neutral-50"
              autoFocus
            />
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 text-[11px] text-neutral-500">
            <span>{query ? `${matches.length} matching product route${matches.length === 1 ? '' : 's'}` : 'Popular product searches'}</span>
            <span>{catalog.length ? `${catalog.length} products indexed` : ''}</span>
          </div>

          <div className="mt-4 space-y-3">
            {loading && (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-neutral-500">
                <LoaderCircle className="w-4 h-4 animate-spin" /> Building the product route index…
              </div>
            )}
            {!loading && matches.map((item) => (
              <article key={`${item.productHref}-${item.product}`} className="group p-4 rounded-2xl bg-white hover:bg-rose-50/40 border border-neutral-100 hover:border-rose-200 transition-all">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-display text-base font-bold text-neutral-900">{item.product}</div>
                    {(item.standard || item.category) && (
                      <div className="text-xs text-neutral-500 mt-1">{[item.standard, item.category].filter(Boolean).join(' · ')}</div>
                    )}
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold mt-1.5">
                      {item.generated ? 'Preliminary compliance route' : 'Likely compliance routes'}
                      {item.sourceLabel ? ` · ${item.sourceLabel}` : ''}
                    </div>
                  </div>
                  <a href={item.productHref} onClick={() => onOpenChange(false)} className="inline-flex items-center gap-1 text-xs font-bold text-[#C41E3A] shrink-0">
                    {item.generated ? 'Confirm route' : 'Product guide'} <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {item.approvals.map((approval) => (
                    <a
                      key={`${item.product}-${approval.approval}`}
                      href={approval.href}
                      onClick={() => onOpenChange(false)}
                      title={approval.note || `Open ${approval.approval}`}
                      className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-full bg-rose-50 text-[#C41E3A] border border-rose-100 font-semibold hover:bg-[#C41E3A] hover:text-white transition-colors"
                    >
                      <Route className="w-3 h-3" /> {approval.approval}
                    </a>
                  ))}
                </div>
              </article>
            ))}
            {!loading && query && matches.length === 0 && (
              <div className="text-sm text-neutral-500 text-center py-8">
                This product is not yet in the website index. <a href="/contact" onClick={() => onOpenChange(false)} className="text-[#C41E3A] font-semibold underline">Ask Complise for manual route mapping</a>.
              </div>
            )}
          </div>

          <p className="mt-6 text-[11px] text-neutral-500 text-center">
            Indicative routes only. Final requirements depend on specifications, intended use, applicant type and import/manufacturing model.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AISearch;
